import { useMultipleUserProfiles } from "@jugl-web/domain-resources/users/hooks/useMultipleUserProfiles";
import { AvatarStack } from "@jugl-web/ui-components/cross-platform/AvatarStack";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { TicketAssigneesPickerPopover } from "@web-src/modules/client-support/components/TicketAssigneesPickerPopover";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  TicketDetailItem,
  TicketDetailItemPlaceholder,
} from "../TicketDetailItem";

interface AssigneesDetailItemProps {
  assigneeIds: string[];
  onChange: (assigneeIds: string[]) => void;
}

export const AssigneesDetailItem: FC<AssigneesDetailItemProps> = ({
  assigneeIds,
  onChange,
}) => {
  const { entityId } = useEntitySelectedProvider();

  const [internalAssigneeIds, setInternalAssigneeIds] =
    useState<string[]>(assigneeIds);

  const internalAssigneeIdsSorted = useMemo(
    () => [...internalAssigneeIds].sort(),
    [internalAssigneeIds]
  );

  const { profiles: assigneeProfiles } = useMultipleUserProfiles({
    entityId,
    userIds: internalAssigneeIdsSorted,
  });

  const handleSelectionConfirm = useCallback(
    (selectedAssigneeIds: string[]) => {
      onChange(selectedAssigneeIds);
    },
    [onChange]
  );

  // Sync internal assignees with ticket assignees whenever it changes
  useEffect(() => {
    setInternalAssigneeIds(assigneeIds);
  }, [assigneeIds]);

  return (
    <TicketAssigneesPickerPopover
      assigneeIds={assigneeIds}
      onSelectionChange={setInternalAssigneeIds}
      onSelectionConfirm={handleSelectionConfirm}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          as={TicketDetailItem}
          ref={triggerRef}
          isInteractive
          isActive={isOpen}
          label="Assignees"
          content={
            assigneeProfiles.length > 0 ? (
              <AvatarStack
                size="sm"
                maxCount={6}
                members={assigneeProfiles.map((profile) => ({
                  firstName: profile.firstName || "",
                  lastName: profile.lastName || "",
                  imageUrl: profile.avatar || "",
                }))}
                shouldShowNameForSingleMember
                singleMemberLabelClassName="ml-2 text-[13px] leading-[1.4] font-normal text-dark"
              />
            ) : (
              <TicketDetailItemPlaceholder />
            )
          }
        />
      )}
    />
  );
};
